import { h, Fragment } from 'preact';
import {
	useMemo, useState, useRef, useEffect,
} from 'preact/compat';
import type { Target } from '../../types';
import {
	useCart, CartActionType, normalizeCurrency, formatCurrency,
} from '../../hooks/cart';

/**
 * Generic custom amount input.
 * Safely handles dispatching actions to the cart.
 *
 * @param props.target The candidate associated with dispatch.
 * @param props.matchValue Whether to always track the value to the
 * candidate selection, regardless of manualInput flag.
 * @param props.className Optional classnames to add to outer div.
 */
const CustomAmount = ({
	target,
	matchValue = false,
	className = '',
	forceActive = false,
	id = '',
	followUpAction = null,
}: {
	target: Target;
	matchValue?: boolean;
	className?: string;
	forceActive?: boolean;
	id?: string;
	followUpAction?: () => void;
}) => {
	const { dispatch, cart } = useCart();
	const { selections } = cart;

	/**
	 * Value only if exists in map,
	 * and has manualInput.
	 *
	 * 'matchValue' forces the value to track selection amount,
	 * regardless of if it is flagged as 'manualInput'
	 */
	const match = matchValue === true
		? selections.has(target.id) : selections.has(target.id)
		&& selections.get(target.id).manualInput;
	const value = match ? selections.get(target.id).amount : null;

	/**
	 * Update event.
	 * Handle removal if user sets value to 0.
	 *
	 * Calls followUpAction if provided only when value is updated to > 0.
	 */
	const updateDispatch = useMemo(() => (inputValue: number | string) => {
		const val = normalizeCurrency(inputValue).value;
		if (val <= 0) {
			dispatch({
				type: CartActionType.REMOVE_SELECTION,
				payload: {
					id: target.id,
				},
			});
		} else {
			dispatch({
				type: CartActionType.MANUAL_INPUT,
				payload: {
					...target,
					amount: val,
				},
			});
			/**
			 * Follow up action.
			 * Use case: Upsell setState to final confirm.
			 */
			followUpAction?.();
		}
	}, [dispatch, target, followUpAction]);

	/**
	 * Handle enter event.
	 */
	const handleKeyDown = (e: KeyboardEvent) => {
		if ((e.key === 'Enter' || e.keyCode === 13) && e.target instanceof HTMLInputElement) {
			updateDispatch(e.target.value);
		}
	};

	/**
	 * Handle active state.
	 * Usually on btn click.
	 * If active state is coming from parent, do not autofocus.
	 */
	const [active, setActive] = useState(forceActive);
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (active && !forceActive) {
			inputRef?.current.focus();
		}
	}, [active, forceActive]);

	return (
		<div
			className={[
				'ea-custom-amount',
				className,
				active ? 'ea-custom-amount--active' : '',
			].join(' ')}
			data-active={value > 0}
		>
			{!active ? (
				<button
					type="button"
					className="ea-custom-amount__btn"
					onClick={() => {
						setActive(true);
					}}
					data-active={value > 0}
				>
					{value > 0 ? (
						<span>{formatCurrency(value)}</span>
					) : (
						<Fragment>
							<span className="ea-custom-amount__usd">$</span>
							<span>Other</span>
						</Fragment>
					)}
				</button>
			) : (
				<div className="ea-custom-amount__input-wrap">
					<span className="ea-custom-amount__usd">$</span>
					<input
						id={id}
						min={0}
						type="number"
						value={value}
						ref={inputRef}
						/**
						 * We trigger update on focus out event
						 * to reduce layout jump while changing the value.
						 */
						onBlur={(e) => {
							updateDispatch(e.currentTarget.value);
							if (!forceActive) {
								setActive(false);
							}
						}}
						/**
						 * Trigger update on 'enter' keydown event.
						 */
						onKeyDown={handleKeyDown}
						step="0.01"
					/>
				</div>
			)}
		</div>
	);
};

export default CustomAmount;
